import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import { fetchSafetyOverview } from "../../../views/Safety/safetyApi";
import WidgetFrame, { WidgetEmpty, WidgetStat } from "./WidgetFrame";

// Safety widgets (Admin / Safety Manager only, via catalog roles — the API
// also checks the x-user-id header server-side). One per Safety page, all
// fed by the same overview call the Safety Overview page uses.
const useSafetyOverview = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    fetchSafetyOverview(user.id)
      .then(setOverview)
      .catch(() => setFailed(true));
  }, [user?.id]);

  return { overview, failed };
};

const SafetyTile = ({ title, to, stats }) => {
  const { overview, failed } = useSafetyOverview();
  return (
    <WidgetFrame title={title} to={to}>
      {overview ? (
        <div className="grid grid-cols-3 gap-2">
          {stats(overview).map(([value, label]) => (
            <WidgetStat key={label} value={value} label={label} />
          ))}
        </div>
      ) : (
        <WidgetEmpty>{failed ? "Couldn't load safety data." : "Loading safety data…"}</WidgetEmpty>
      )}
    </WidgetFrame>
  );
};

const SafetyOverviewWidget = () => (
  <SafetyTile
    title="Safety Overview"
    to="/safety/overview"
    stats={(o) => [
      [o.concerns.open + o.concerns.inReview, "Open concerns"],
      [o.extinguishers.due, "Extinguishers due"],
      [o.training.overdue, "Training overdue"],
    ]}
  />
);

export const SafetyConcernsWidget = () => (
  <SafetyTile
    title="Safety Concerns"
    to="/safety/concerns"
    stats={(o) => [
      [o.concerns.open, "Open"],
      [o.concerns.inReview, "In review"],
      [o.concerns.reportedRecently, `Reported (${o.rules.recentDays}d)`],
    ]}
  />
);

export const FireExtinguishersWidget = () => (
  <SafetyTile
    title="Fire Extinguishers"
    to="/safety/extinguishers"
    stats={(o) => [
      [o.extinguishers.due, "Due"],
      [o.extinguishers.neverInspected, "Never inspected"],
      [o.extinguishers.failed, "Failed last check"],
    ]}
  />
);

export const TrainingVideosWidget = () => (
  <SafetyTile
    title="Training Videos"
    to="/safety/training"
    stats={(o) => [
      [o.training.overdue, "Overdue"],
      [o.training.dueSoon, "Due this week"],
      [
        o.training.assigned ? `${Math.round((o.training.completed / o.training.assigned) * 100)}%` : "—",
        "Done",
      ],
    ]}
  />
);

export const ToolboxResultsWidget = () => (
  <SafetyTile
    title="Toolbox Talk Results"
    to="/safety/toolbox-results"
    stats={(o) => {
      const latest = o.toolbox.latest;
      return [
        [latest ? latest.done : "—", "Finished latest"],
        [latest?.quizEnabled ? `${latest.quizPassed}/${latest.quizTaken}` : "—", "Passed quiz"],
        [latest ? new Date(latest.createdAt).toLocaleDateString([], { month: "short", day: "numeric" }) : "—", "Posted"],
      ];
    }}
  />
);

export default SafetyOverviewWidget;
